<script setup lang="ts" generic="T">
import { computed, inject, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { tabsPanelIdKey, tabsRegistryKey } from './internal';

const props = defineProps<{ id: string; name: string; info: T }>();

const registry = inject(tabsRegistryKey);
if (!registry) throw new Error('[nx-ui] TabPanel must be used inside TabsRoot');

const idRef = ref(props.id);
watch(
  () => props.id,
  (v) => (idRef.value = v)
);
provide(tabsPanelIdKey, idRef);

let unregister: (() => void) | undefined;
onMounted(() => {
  unregister = registry.register({ id: props.id, name: props.name, info: props.info });
});
// Re-register on metadata change (updates in place, keeps order); on id change,
// drop the old registration first so the registry does not keep a stale tab.
watch(
  () => ({ id: props.id, name: props.name, info: props.info }),
  (cur, prev) => {
    if (prev && prev.id !== cur.id) unregister?.();
    unregister = registry.register(cur);
  }
);
onBeforeUnmount(() => unregister?.());

const active = computed(() => registry.selectedId.value === props.id);
</script>

<template>
  <!-- Only the active panel is rendered; <KeepAlive> caches an inactive body's
       state (its single root component) and restores it on return. The TabPanel
       component itself stays mounted, so registration persists for all tabs. -->
  <KeepAlive>
    <slot v-if="active" />
  </KeepAlive>
</template>
